import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { ConnectionStatus } from "../types";

/**
 * Invalidates all queries when WebSocket connection becomes CONNECTED.
 *
 * Race condition: API queries execute immediately on component render,
 * while the RSocket connection takes some time to establish. WebSocket
 * subscriptions only start after CONNECTED status. Any backend updates
 * during this window are missed because no subscription is listening yet.
 *
 * Solution: Refetch all cached data when WebSocket connects, ensuring
 * the cache reflects the latest server state before subscriptions start
 * processing new events.
 */
export function useInvalidateQueriesOnConnect(status: ConnectionStatus) {
    const queryClient = useQueryClient();
    const prevStatusRef = useRef<ConnectionStatus | undefined>(undefined);

    useEffect(() => {
        const prevStatus = prevStatusRef.current;
        const isNowConnected = status === ConnectionStatus.CONNECTED;
        const wasNotConnected = prevStatus !== undefined && prevStatus !== ConnectionStatus.CONNECTED;

        if (isNowConnected && wasNotConnected) {
            console.debug("RSocket connected - invalidating queries to refetch potentially stale data");
            void queryClient.invalidateQueries();
        }

        prevStatusRef.current = status;
    }, [status, queryClient]);
}
